<template>
    <!-- Outside the section on purpose: aria-busy on the section tells VoiceOver to hold live
         updates inside it until the load ends, by which time this text is already gone. -->
    <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ loadingAnnouncement }}</span>
    <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ checkAnnouncement || assessmentAnnouncement }}</span>
    <section ref="section" class="flex flex-col gap-6" :aria-label="t('connections.pairPerspective.sectionLabel')" :aria-busy="isFetching">
        <p v-if="isFetching && !retrying" class="body-small text-ink-muted">{{ t("connections.pairPerspective.loading") }}</p>
        <!-- The pressed button stays mounted while the retry runs: removing a focused control drops
             VoiceOver to the page and the loading announcement with it. -->
        <div v-else-if="error || retrying" class="flex flex-col items-start gap-3">
            <p v-if="retrying" class="body-small text-ink-muted">{{ t("connections.pairPerspective.loading") }}</p>
            <p v-else role="alert" class="body-small">{{ t("connections.pairPerspective.loadError") }}</p>
            <BaseButton theme="neutral" size="sm" :disabled="retrying" @click="retryComparison">{{ t("common.tryAgain") }}</BaseButton>
        </div>
        <template v-else-if="data?.comparison">
            <PairPerspectiveCards
                :comparison="data.comparison"
                :advice="advice"
                :is-generating="isGenerating"
                :advice-error="adviceError ? t('connections.pairPerspective.adviceError') : null"
                :is-starting-chat="isStartingChat"
                :chat-error="chatError"
                @retry="generateAdvice"
                @discuss="emit('discuss', $event)" />
            <PairPerspectiveDimensions :comparison="data.comparison" :is-starting-chat="isStartingChat" :chat-error="chatError" @discuss="emit('discuss', $event)" />
        </template>
        <div v-else-if="data?.unavailable_reason" class="flex min-h-106 flex-col items-center justify-center rounded-lg border border-border bg-surface p-6 text-center">
            <h3
                tabindex="-1"
                data-unavailable-title
                class="body-serif text-ink rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-control-accent focus-visible:ring-offset-2">
                {{ t("connections.pairPerspective.unavailableTitle") }}
            </h3>
            <p class="body-small mt-1 text-ink-muted">{{ data.unavailable_reason }}</p>
            <!-- Same as the comparison retry: the pressed button stays mounted while the check reruns. -->
            <div v-if="assessmentCheckFailed" class="mt-1 flex flex-col items-center gap-3">
                <p v-if="checkingAssessment" class="body-small text-ink-muted">{{ t("connections.pairPerspective.assessmentChecking") }}</p>
                <p v-else role="alert" class="body-small text-ink-muted">{{ t("connections.pairPerspective.assessmentCheckError") }}</p>
                <BaseButton theme="neutral" size="sm" :disabled="checkingAssessment" @click="retryAssessmentCheck">{{ t("common.tryAgain") }}</BaseButton>
            </div>
            <p v-else-if="checkingAssessment" class="body-small mt-4 text-ink-muted">{{ t("connections.pairPerspective.assessmentChecking") }}</p>
            <BaseLink v-else-if="availability?.offer_assessment" href="/perspective/" data-complete-perspective class="base-btn-neutral base-btn-sm mt-4">{{
                t("connections.pairPerspective.completePerspective")
            }}</BaseLink>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { PairAdvicePending, PairAdviceResponse, PairPerspectiveResponse } from "~vue/api/generated/pair-perspective";
import type { PairAvailability } from "~vue/api/generated/perspectives";
import BaseButton from "~vue/components/BaseButton.vue";
import { useLoadingAnnouncement } from "~vue/composables/useLoadingAnnouncement";
import PairPerspectiveCards from "~vue/Connections/PairPerspectiveCards.vue";
import PairPerspectiveDimensions from "~vue/Connections/PairPerspectiveDimensions.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ url: string; connectionId: string; isStartingChat: boolean; chatError: string | null }>();
const emit = defineEmits<{ discuss: [message: string] }>();
const { t } = useI18n();

const { data, error, isFetching, execute, abort } = useFetch(props.url, { immediate: false }).get().json<PairPerspectiveResponse>();
const retrying = ref(false);
const loadingAnnouncement = useLoadingAnnouncement(isFetching, () => (retrying.value ? t("connections.pairPerspective.retrying") : t("connections.pairPerspective.loading")));
const advice = ref<PairAdviceResponse | null>(null);
const isGenerating = ref(false);
const adviceError = ref(false);
const requestBody = ref({ retry: false });
const {
    data: result,
    error: generationError,
    execute: generate,
    abort: abortAdvice,
    statusCode: adviceStatus,
} = useFetch(props.url, { immediate: false }).post(requestBody).json<PairAdviceResponse | PairAdvicePending>();

let disposed = false;
let pollTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

const {
    data: availability,
    error: assessmentCheckError,
    execute: checkAvailability,
    abort: abortAssessmentCheck,
} = useFetch(`/v1/perspectives/pair/${props.connectionId}/availability`, { immediate: false }).get().json<PairAvailability>();
const checkingAssessment = ref(false);
const retryingAssessment = ref(false);
const assessmentCheckFailed = ref(false);
const assessmentAnnouncement = ref("");
const checkAnnouncement = useLoadingAnnouncement(checkingAssessment, () =>
    retryingAssessment.value ? t("connections.pairPerspective.assessmentRechecking") : t("connections.pairPerspective.assessmentChecking"),
);
const section = useTemplateRef<HTMLElement>("section");

async function runAssessmentCheck() {
    assessmentAnnouncement.value = "";
    checkingAssessment.value = true;
    try {
        await checkAvailability();
    } finally {
        checkingAssessment.value = false;
    }
    if (!disposed) {
        assessmentCheckFailed.value = !!assessmentCheckError.value || !availability.value;
        if (!assessmentCheckFailed.value && availability.value?.offer_assessment) {
            assessmentAnnouncement.value = t("connections.pairPerspective.assessmentCheckRecovered");
        }
    }
}

// The focused Try again button unmounts when the check recovers, so hand focus to what replaces it:
// the assessment link when there is one, otherwise the empty state's heading.
async function retryAssessmentCheck() {
    retryingAssessment.value = true;
    try {
        await runAssessmentCheck();
    } finally {
        retryingAssessment.value = false;
    }
    if (disposed || assessmentCheckFailed.value) {
        return;
    }
    const offered = !!availability.value?.offer_assessment;
    await nextTick();
    section.value?.querySelector<HTMLElement>(offered ? "[data-complete-perspective]" : "[data-unavailable-title]")?.focus();
}

// coach_api owns why the pair is unavailable and whether to offer the assessment; Django only serves the comparison.
watch(
    () => data.value && !data.value.comparison,
    (unavailable) => {
        if (unavailable && !disposed) {
            void runAssessmentCheck();
        }
    },
);

async function pollAdvice() {
    await generate();
    if (disposed) {
        return;
    }
    requestBody.value = { retry: false };
    if (adviceStatus.value === 404 || adviceStatus.value === 409) {
        data.value = null;
        advice.value = null;
        isGenerating.value = false;
        await execute();
        return;
    }
    if (generationError.value || !result.value) {
        adviceError.value = true;
        isGenerating.value = false;
        return;
    }
    if (!("interpretation" in result.value)) {
        pollTimer = globalThis.setTimeout(pollAdvice, 1500);
        return;
    }
    advice.value = result.value;
    isGenerating.value = false;
}

async function generateAdvice() {
    if (disposed || isGenerating.value) {
        return;
    }
    adviceError.value = false;
    isGenerating.value = true;
    requestBody.value = { retry: true };
    await pollAdvice();
}

async function loadComparison() {
    await execute();
    if (!disposed && data.value?.comparison) {
        await generateAdvice();
    }
}

async function retryComparison() {
    retrying.value = true;
    try {
        await execute();
    } finally {
        retrying.value = false;
    }
    if (!disposed && data.value?.comparison) {
        await generateAdvice();
    }
}

onMounted(loadComparison);
onBeforeUnmount(() => {
    disposed = true;
    abort();
    abortAdvice();
    abortAssessmentCheck();
    globalThis.clearTimeout(pollTimer);
});
</script>
